import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Maximize2, X } from 'lucide-react'
import { PageHeader, StatTile, Panel, Chip, Button, BarChart } from '../ds/index'
import { HBars } from '../components/Viz'
import { Icon } from '../components/Icon'
import { BrandMark } from '../components/BrandMark'
import { useApi } from '../lib/useApi'
import { useSession, isAdmin } from '../lib/session'
import { num, ago, dateShort, dateLong, timeShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Count = { posts: number; trades: number; other: number; requests: number }
type FeedItem = { id: string; first_seen_at: string; trade: boolean; service: string | null; area: string | null; group_name: string | null; summary: string | null }
type Flagged = { id: string; first_seen_at: string; stage: string; suggested_category: string | null; screen_reason: string | null; group_name: string | null; yes_by: string[]; no_count: number }
export type Flow = {
  timezone: string
  today: Count
  week: Count & { per_day: number }
  all_time: { requests: number; since: string | null }
  daily: { day: string; trades: number; other: number }[]
  services_7d: { name: string; trade: boolean; n: number }[]
  areas_7d: { name: string; n: number }[]
  feed: FeedItem[]
  feedback_7d: { yes: number; no: number; flagged_posts: number }
  flagged: Flagged[]
}

const REFRESH_MS = 30_000
const SERIES = [{ key: 'trades', label: 'In our trades' }, { key: 'other', label: 'Other services' }]

/** What the request was for, in a few words. Never the poster's words or name: this page is shown to outsiders. */
const what = (f: FeedItem) => f.trade && f.summary ? f.summary : `Looking for ${(f.service || 'a local service').toLowerCase()}`

/**
 * Every request coming in from the groups, live. One page for admins and reps;
 * Present turns it into a full-screen view to show someone Handpicked wants to
 * hire or sell to. Nothing on it names a poster or quotes a post.
 */
export default function FlowPage() {
  const { me } = useSession()
  const [params, setParams] = useSearchParams()
  const presenting = params.get('present') === '1'
  const { data, error, reload } = useApi<Flow>('/api/flow')
  const [at, setAt] = useState(() => new Date())

  useEffect(() => {
    const t = setInterval(() => { reload(); setAt(new Date()) }, REFRESH_MS)
    return () => clearInterval(t)
  }, [reload])

  const present = (on: boolean) => {
    setParams((p) => { const n = new URLSearchParams(p); if (on) n.set('present', '1'); else n.delete('present'); return n }, { replace: true })
    // Full screen needs the tap that asked for it; a browser that refuses still gets the clean view.
    try {
      if (on && !document.fullscreenElement) void document.documentElement.requestFullscreen?.().catch(() => {})
      if (!on && document.fullscreenElement) void document.exitFullscreen?.().catch(() => {})
    } catch { /* full screen is optional */ }
  }

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  if (presenting) return <Presentation data={data} at={at} onExit={() => present(false)} />

  const admin = isAdmin(me)
  const fb = data.feedback_7d
  return (
    <>
      <PageHeader
        title="Lead flow"
        subtitle="Every request coming in from the groups, live. Present it to show someone how many people ask for help each day."
        actions={<Button onClick={() => present(true)}><Icon of={Maximize2} size={15} /> Present</Button>}
      />

      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Requests today" value={num(data.today.requests)} meta={`${num(data.today.trades)} in our trades, ${num(data.today.other)} other services`} />
        <StatTile label="Last 7 days" value={num(data.week.requests)} meta={`about ${num(data.week.per_day, data.week.per_day < 10 ? 1 : 0)} a day`} />
        <StatTile label="Posts read today" value={num(data.today.posts)} meta="every post in the groups, each counted once" />
        <StatTile label="Requests found so far" value={num(data.all_time.requests)} meta={data.all_time.since ? `since ${dateShort(data.all_time.since)}` : 'none yet'} />
      </div>

      <div className="wide-narrow">
        <Panel>
          <BarChart title="Requests, the last 14 days" subtitle="People asking for a local service. In a trade Handpicked sells, or one it does not cover yet."
                    height={260} stacked integer showTable xKey="day" series={SERIES}
                    data={data.daily.map((d) => ({ ...d, day: dateShort(d.day) }))} />
        </Panel>
        <Panel>
          <HBars title="What people ask for" subtitle="Last 7 days" format={(n) => num(n)} empty="Nothing this week yet."
                 rows={data.services_7d.map((s) => ({ label: s.name, value: s.n, note: s.trade ? undefined : 'not covered yet' }))} />
        </Panel>
      </div>

      <div className="wide-narrow">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Coming in now</h2><span className="muted">updated {timeShort(at)}</span></div>
          <Feed items={data.feed} showGroup limit={15} />
        </Panel>
        <div className="stack">
          <Panel>
            <HBars title="Where" subtitle="Last 7 days, requests that named an area" format={(n) => num(n)} slot={2} empty="No area named this week."
                   rows={data.areas_7d.map((a) => ({ label: a.name, value: a.n }))} />
          </Panel>
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Reps checking the AI</h2><span className="muted">last 7 days</span></div>
            {fb.yes + fb.no === 0 ? (
              <p className="muted" style={{ margin: 0 }}>No answers yet. The posts the AI held back reach reps in My leads, marked as held back.</p>
            ) : (
              <p style={{ margin: 0 }}>Reps answered on <strong className="num">{num(fb.yes + fb.no)}</strong> posts the AI held back and said <strong className="num">{num(fb.flagged_posts)}</strong> {fb.flagged_posts === 1 ? 'was a lead' : 'were leads'}.</p>
            )}
            {admin && data.flagged.length > 0 && (
              <ul className="flagged-list">
                {data.flagged.map((f) => (
                  <li key={f.id}>
                    <Link to={`/leads/${f.id}`}><strong>{f.suggested_category || (f.stage === 'not_lead' ? 'AI said not a request' : 'Not covered')}</strong></Link>
                    <span className="muted"> · {f.group_name || 'Unknown group'} · {ago(f.first_seen_at)}</span>
                    <div className="muted" style={{ fontSize: 'var(--t-ui)' }}>Lead, says {f.yes_by.join(', ')}{f.no_count ? `; ${f.no_count} said no` : ''}. AI: {f.screen_reason}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </>
  )
}

function Feed({ items, showGroup = false, limit }: { items: FeedItem[]; showGroup?: boolean; limit?: number }) {
  const shown = limit ? items.slice(0, limit) : items
  if (!shown.length) return <p className="muted" style={{ margin: 0 }}>Nothing yet. Requests show up here within a minute of the post.</p>
  return (
    <ul className="flow-feed">
      {shown.map((f) => (
        <li key={f.id} className="flow-item">
          <span className="flow-when nowrap" title={new Date(f.first_seen_at).toLocaleString()}>{ago(f.first_seen_at)}</span>
          <span className="flow-what">{what(f)}</span>
          <span className="flow-tags">
            {f.service && <Chip tone={f.trade ? 'accent' : undefined}>{f.service}</Chip>}
            {f.area && <Chip>{f.area}</Chip>}
            {showGroup && f.group_name && <span className="muted flow-group">{f.group_name}</span>}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Full screen, nothing to click but Exit. No group names, no links, no prices. */
function Presentation({ data, at, onExit }: { data: Flow; at: Date; onExit: () => void }) {
  const { me } = useSession()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.fullscreenElement) onExit() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onExit])

  return (
    <div className="flow-present" role="dialog" aria-label="Lead flow, presentation">
      <header className="flow-present-top">
        <BrandMark fallbackName={me?.org.name || 'Leads'} />
        <span className="flow-live"><i aria-hidden="true" /> Live · {dateLong(at)} · {timeShort(at)}</span>
        <Button variant="ghost" onClick={onExit} aria-label="Exit presentation"><Icon of={X} size={18} /> Exit</Button>
      </header>

      <section className="flow-hero">
        <div className="flow-big">
          <span className="flow-big-num">{num(data.today.requests)}</span>
          <span className="flow-big-label">people asked for help today</span>
        </div>
        <div className="flow-big">
          <span className="flow-big-num">{num(data.week.requests)}</span>
          <span className="flow-big-label">in the last 7 days</span>
        </div>
        <div className="flow-big">
          <span className="flow-big-num">{num(data.week.per_day, data.week.per_day < 10 ? 1 : 0)}</span>
          <span className="flow-big-label">a day on average</span>
        </div>
      </section>

      <section className="flow-present-grid">
        <div className="flow-present-panel">
          <BarChart title="Every day, the last 14 days" height={300} stacked integer xKey="day" series={SERIES}
                    data={data.daily.map((d) => ({ ...d, day: dateShort(d.day) }))} />
          {data.services_7d.length > 0 && (
            <div className="flow-asks">
              <span className="flow-asks-label">What people ask for this week</span>
              <div className="flow-asks-chips">
                {data.services_7d.map((s) => <span key={s.name} className="flow-ask">{s.name} <b className="num">{num(s.n)}</b></span>)}
              </div>
            </div>
          )}
        </div>
        <div className="flow-present-panel">
          <h2 className="flow-present-h">Coming in now</h2>
          <Feed items={data.feed} limit={9} />
        </div>
      </section>

      <footer className="flow-present-foot">
        Real requests posted in local Facebook groups, found and sorted as they arrive.{data.all_time.since ? ` ${num(data.all_time.requests)} since ${dateShort(data.all_time.since)}.` : ''}
      </footer>
    </div>
  )
}
